/**
 * training-class-edit-date.js
 */

import React from 'react';
import { Divider, Popover, ButtonGroup } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parse, addYears } from 'date-fns';
import { formatDurationHours, dateParamFormat } from 'utils/datetime';
import { renderForMobileContext } from 'utils/app-context';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import Button from 'components/button';
import Icon from 'components/icon';
import DateTimePicker from 'components/form-inputs/date-time-picker';
import TextField from 'components/form-inputs/text-field';


const useStyles = makeStyles(theme => ({
  duration: {
    maxWidth: '150px !important',
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      maxWidth: '200px !important',
    }
  },
  durationAdornmentContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    '& .hours': {
      height: '100%',
      backgroundColor: theme.palette.sys.surfaceContainerHigh,
      padding: `0 ${theme.spacing(1)}`,
      fontSize: theme.typography.sizes[2],
    },
    '& .buttons': {
      display: 'flex',
      flexDirection: 'column',
      [theme.size.menuDrawer.mediaRuleMobileContent]: {
        flexDirection: 'row'
      },
      '& > .MuiButtonBase-root': {
        flex: 1,
        padding: 0,
        minWidth: theme.spacing(6),
        maxHeight: 20,
      },
      '& > *:first-child': {
        transform: 'rotate(180deg)',
        top: 2,
        [theme.size.menuDrawer.mediaRuleMobileContent]: {
          top: 0,
        },
      },
      '& > *:last-child': {
        top: -1,
        [theme.size.menuDrawer.mediaRuleMobileContent]: {
          top: 0
        },
      }
    }
  },
  bgButton: {
    fontSize: theme.typography.sizes[5],
  }
}));


export default function TrainingClassEditDate(props) {
  const {
    storedRecord,
    drawerState,
    translation,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation(translation);
  const [durationMenuAnchor, setDurationMenuAnchor] = React.useState();
  const { control, setValue } = useFormContext();
  const durationValue = useWatch({ control, name: 'duration' });
  const setValProps = {
    shouldDirty: true
  };


  // if mobile and creating, use selected calendar date. If no date selected, use current time
  React.useEffect(() => {
    if (storedRecord.id === 0 && renderForMobileContext()) {
      const dateParam = new URLSearchParams(window.location.search).get('date');
      const date = dateParam ? parse(dateParam, dateParamFormat, new Date()) : new Date();
      setValue('date', date);
    }
  }, [storedRecord, setValue]);

  const incrementDuration = (hours) => {
    const newDuration = durationValue + hours;
    setValue('duration', newDuration || 0.25, setValProps);
  }

  const setDuration = (hours) => {
    setValue('duration', hours, setValProps);
  }

  return (
    <>
      <EditorIconSection
        iconName='event'
        helpProps={{
          translation,
          section: 'dateTime'
        }}
      >
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('dateTime.dateTime.helpTitle')}
          text={t('dateTime.dateTime.helpInfo')}
        >
          {focusHandlers =>
            <>
              <div data-testid="class-datetime">
                <FormGridContainer>

                  {/* date time picker */}
                  <FormGridItem md={7}>
                    <DateTimePicker
                      variant='datetime'
                      name='date'
                      label={t('dateTime.dateTime.label')}
                      focusHandlers={focusHandlers}
                      format="MM/dd/yyyy HH:mm"
                      showTodayButton={true}
                      rules={{ 
                        required: t('dateTime.dateTime.errMsgEmpty'),
                      }}
                      maxDate={addYears(new Date(), 1)}
                      maxDateMessage={t('dateTime.dateTime.errMsgMaxDate')}
                      minDate={addYears(new Date(), -20)}
                      minDateMessage={t('dateTime.dateTime.errMsgMinDate')}
                    />
                  </FormGridItem>

                  {/* duration */}
                  <FormGridItem md={5}>
                    <TextField
                      name='duration'
                      label={t('dateTime.duration.label')}
                      className={classes.duration}
                      displayValueAs={value => formatDurationHours(value)}
                      focusHandlers={focusHandlers}
                      onClick={e => setDurationMenuAnchor(e.currentTarget)}
                      onKeyDown={e => {
                        e.stopPropagation();
                        e.preventDefault(); 
                        setDurationMenuAnchor(e.currentTarget); 
                      }}
                      InputProps={{
                        endAdornment: <div className={`flex-hc ${classes.durationAdornmentContainer}`}>
                          <div className='flex-hc hours'>
                            <Icon type='date' />
                          </div>
                          <div className='buttons'>
                            <Button>
                              <Icon
                                type='expander'
                                onClick={e => {
                                  e.stopPropagation();
                                  incrementDuration(0.25);
                                }}
                              />
                              </Button>
                            <Divider />
                            <Button>
                              <Icon
                                type='expander'
                                onClick={e => {
                                  e.stopPropagation();
                                  incrementDuration(-0.25);
                                }}
                              />
                            </Button>
                          </div>
                        </div>
                      }}
                    />
                  </FormGridItem>
                </FormGridContainer>
              </div>

              <Popover
                open={Boolean(durationMenuAnchor)}
                anchorEl={durationMenuAnchor}
                onClose={() => setDurationMenuAnchor()}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'center',
                }}
              >
                <ButtonGroup
                  aria-label="outlined secondary button group"
                  disableElevation
                  className={classes.buttonGroup}
                >
                  {[1,2,3,4,6,8].map(hours =>
                    <Button
                      key={hours}
                      className={classes.bgButton}
                      onClick={() => {
                        setDuration(hours);
                        setDurationMenuAnchor();
                      }}
                    >
                      {hours}
                    </Button>)}
                </ButtonGroup>
              </Popover>
            </>}

        </HelpTextItem>
      </EditorIconSection>
    </>
  );
}